import { useEffect, useState } from "react";

import { $convertToMarkdownString } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Close, Send, Info } from "@mui/icons-material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Template } from "@rubi/api-generated";
import { useTranslation } from "react-i18next";

import LexicalEditor, { customTransformers } from "@/common/LexicalEditor";
import { RubiModal } from "@/common/RubiModal";
import { supportedLanguages } from "@/common/utils";
import containerStyles from "@/components/online-reporting/report-view/CondensedReportView.module.scss";
import { reportColourTypes } from "@/types/typesReportV2";

import actionModalStyles from "./ActionModal.module.scss";
import {
  useActionModalTranslation,
  splitBilingualMarkdownParts,
  buildBilingualMarkdown,
} from "./useActionModalTranslation";

const TRANSLATION_DISCLAIMER =
  "Machine-generated translation may not be 100% accurate.";

interface IModalDetails {
  title: string;
  description: string | JSX.Element;
  detailsRequired: boolean;
  detailsText: string;
  rteOptionalText?: string;
}

interface ActionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  selectedAction: string;
  modalDetails: { [key: string]: IModalDetails };
  specifiedReason: string;
  setSpecifiedReason: (reason: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  isReopen: boolean;
  // Intel app only: gates the "Notify Reporting Party" option on approval so it
  // doesn't appear when approving through reports or registries.
  isIntelApp: boolean;
  notifyReportingParty: boolean;
  setNotifyReportingParty: (notify: boolean) => void;
  assignReopenToSelf: boolean;
  setAssignReopenToSelf: (assign: boolean) => void;
  emailTemplates: Template[];
  selectedTemplate: Template | null;
  setSelectedTemplate: (template: Template | null) => void;
  onClose: () => void;
}

const ActionModal = ({
  isModalOpen,
  setIsModalOpen,
  selectedAction,
  modalDetails,
  specifiedReason,
  setSpecifiedReason,
  isSubmitting,
  setIsSubmitting,
  isReopen,
  isIntelApp,
  notifyReportingParty,
  setNotifyReportingParty,
  assignReopenToSelf,
  setAssignReopenToSelf,
  emailTemplates,
  selectedTemplate,
  setSelectedTemplate,
  onClose,
}: ActionModalProps) => {
  const { t } = useTranslation("reportsv2/condensed_report");
  const translation = useActionModalTranslation({
    isModalOpen,
    specifiedReason,
    setSpecifiedReason,
  });

  const [isHeaderInfoOpen, setIsHeaderInfoOpen] = useState(false);

  useEffect(() => {
    if (!isModalOpen) {
      setIsHeaderInfoOpen(false);
    }
  }, [isModalOpen]);

  const handleCancel = () => {
    translation.resetOnClose();
    setIsHeaderInfoOpen(false);
    onClose();
  };

  const headerIconColor = reportColourTypes[selectedAction]
    ? `${reportColourTypes[selectedAction]}.main`
    : "action.active";

  const SubmitButton = () => {
    const [editor] = useLexicalComposerContext();
    return (
      <Button
        variant="contained"
        color="primary"
        className={actionModalStyles.submitButton}
        onClick={() => {
          editor.update(() => {
            const fullMarkdown = $convertToMarkdownString(
              customTransformers,
              undefined,
              true,
            );

            const { originalMarkdown: englishMarkdown } =
              splitBilingualMarkdownParts(fullMarkdown);

            const translationToSend = translation.translatedTextRaw.trim();

            const finalMarkdown = translationToSend
              ? buildBilingualMarkdown(englishMarkdown, translationToSend)
              : englishMarkdown;

            setSpecifiedReason(finalMarkdown);
            setIsSubmitting(true);
          });
        }}
        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        endIcon={!isSubmitting ? <Send /> : null}
        disabled={isSubmitting || translation.isTranslating}
      >
        SUBMIT
      </Button>
    );
  };

  const TranslatedResponseSection = () => {
    const [editor] = useLexicalComposerContext();
    const translationLanguageOptions = supportedLanguages.filter(
      (language) => language.code !== "en",
    );

    useEffect(() => {
      if (!translation.translatedLanguageCode) return;

      const unregister = editor.registerUpdateListener(() => {
        translation.markOutdatedUnlessSuppressed();
      });

      return () => {
        unregister();
      };
    }, [editor]);

    const handleTranslate = (languageCode: string) => {
      let fullMarkdown = "";
      editor.getEditorState().read(() => {
        fullMarkdown = $convertToMarkdownString(
          customTransformers,
          undefined,
          true,
        );
      });
      translation.translate(fullMarkdown, languageCode).catch(() => {});
    };

    const TranslatedEditorSync = () => {
      const [translatedEditor] = useLexicalComposerContext();

      useEffect(() => {
        const unregister = translatedEditor.registerUpdateListener(() => {
          const markdown = translatedEditor
            .getEditorState()
            .read(() =>
              $convertToMarkdownString(customTransformers, undefined, true),
            );
          translation.onTranslatedEditorChange(markdown);
        });

        return () => {
          unregister();
        };
      }, [translatedEditor]);

      return null;
    };

    return (
      <Box className={actionModalStyles.translatedResponseBox}>
        <Autocomplete
          fullWidth
          options={translationLanguageOptions}
          getOptionLabel={(option) => option.name}
          value={
            translationLanguageOptions.find(
              (option) =>
                option.code === translation.translationTargetLanguageCode,
            ) ?? null
          }
          onChange={(_, nextOption) => {
            if (!nextOption) {
              translation.clearTranslation();
              return;
            }

            const nextLang = nextOption.code;
            translation.setTranslationTargetLanguageCode(nextLang);
            handleTranslate(nextLang);
          }}
          disabled={isSubmitting || translation.isTranslating}
          className={actionModalStyles.translateSelect}
          renderInput={(params) => (
            <TextField {...params} label="Translate to" variant="outlined" />
          )}
        />

        {!translation.hasTranslation && translation.isTranslating && (
          <div className={actionModalStyles.translateLoadingInline}>
            <CircularProgress size={18} />
            <Typography
              variant="body2"
              className={actionModalStyles.translateLoadingText}
            >
              Translating...
            </Typography>
          </div>
        )}

        {translation.hasTranslation && (
          <>
            <Divider className={actionModalStyles.translatedDivider} />

            <Box className={actionModalStyles.translatedTitleRow}>
              <Typography variant="subtitle1" fontWeight={700}>
                Translated Response
              </Typography>
              <Chip
                label={translation.translatedLanguageName}
                variant="outlined"
                className={actionModalStyles.translatedChip}
              />
              <Button
                variant="outlined"
                size="small"
                className={actionModalStyles.retranslateButton}
                disabled={isSubmitting || translation.isTranslating}
                onClick={() => {
                  const languageToUse =
                    translation.translatedLanguageCode ??
                    translation.translationTargetLanguageCode;
                  if (!languageToUse) return;

                  translation.setTranslationTargetLanguageCode(languageToUse);
                  handleTranslate(languageToUse);
                }}
              >
                Re-translate
              </Button>
              <IconButton
                size="small"
                aria-label="Clear translated response"
                className={actionModalStyles.translatedClearButton}
                onClick={translation.clearTranslation}
                disabled={isSubmitting || translation.isTranslating}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {translation.isTranslationOutdated && (
              <Typography
                variant="body2"
                className={actionModalStyles.translationOutdatedText}
              >
                <Info fontSize="small" />
                Translation may be outdated. Re-translate after edits.
              </Typography>
            )}

            <div className={actionModalStyles.translatedTextFieldContainer}>
              <LexicalEditor
                markdown={translation.translatedTextRaw}
                readOnly={isSubmitting || translation.isTranslating}
                displayTextEditor
                belowEditorChildren={<TranslatedEditorSync />}
              />

              {translation.isTranslating && (
                <div
                  className={actionModalStyles.translatedTextFieldOverlay}
                  aria-live="polite"
                >
                  <CircularProgress size={20} />
                  <Typography
                    variant="body2"
                    className={actionModalStyles.translatedOverlayText}
                  >
                    Translating...
                  </Typography>
                </div>
              )}
            </div>

            <Typography
              variant="caption"
              className={actionModalStyles.translatedDisclaimer}
            >
              <em>{TRANSLATION_DISCLAIMER}</em>
            </Typography>

            <Divider className={actionModalStyles.translatedSentDivider} />
            <Typography
              variant="body2"
              className={actionModalStyles.translatedSentTogetherText}
            >
              Your response and translated version will be sent together.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <RubiModal
      isModalOpenArg={isModalOpen}
      setIsModalOpenArg={setIsModalOpen}
      onClose={handleCancel}
    >
      <div
        className={`${containerStyles.modalContentContainer} ${actionModalStyles.actionModalRoot}`}
      >
        <div className={actionModalStyles.modalHeader}>
          <span className={actionModalStyles.statusIconWrap}>
            <EditNoteIcon
              className={actionModalStyles.statusIconGlyph}
              sx={{ color: headerIconColor }}
            />
          </span>
          <Typography variant="h6" className={actionModalStyles.headerTitle}>
            {modalDetails[selectedAction]?.title}
          </Typography>
          <IconButton
            size="small"
            aria-label="About this action"
            onClick={() => setIsHeaderInfoOpen((prev) => !prev)}
            className={actionModalStyles.headerInfoButton}
          >
            <Info fontSize="small" />
          </IconButton>
        </div>
        <div className={actionModalStyles.headerRule} />

        {isHeaderInfoOpen && (
          <Typography
            variant="body1"
            className={actionModalStyles.headerDescription}
          >
            {modalDetails[selectedAction]?.description}
          </Typography>
        )}

        {(isReopen || (selectedAction === "approved" && isIntelApp)) && (
          <FormGroup className={actionModalStyles.reopenFormGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyReportingParty}
                  onChange={(e) => {
                    setNotifyReportingParty(e.target.checked);
                  }}
                />
              }
              label={t("notify_reporting_party")}
              className={actionModalStyles.checkboxGrayLabel}
            />
            {isReopen && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={assignReopenToSelf}
                    onChange={(e) => setAssignReopenToSelf(e.target.checked)}
                  />
                }
                label={t("assign_to_self")}
                className={actionModalStyles.checkboxGrayLabel}
              />
            )}
          </FormGroup>
        )}

        {notifyReportingParty && (
          <FormControl fullWidth className={actionModalStyles.templateControl}>
            <Autocomplete
              options={emailTemplates}
              getOptionLabel={(option) => option.name}
              value={selectedTemplate}
              onChange={(event, newValue) => {
                setSelectedTemplate(newValue);
                setSpecifiedReason(newValue?.plain_text ?? "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Template"
                  variant="outlined"
                />
              )}
            />
          </FormControl>
        )}

        <Typography
          variant="subtitle1"
          className={actionModalStyles.responseHeading}
        >
          Response
        </Typography>

        {!!modalDetails[selectedAction]?.detailsText && (
          <Typography className={actionModalStyles.explainText}>
            {modalDetails[selectedAction]?.detailsText}
          </Typography>
        )}

        <LexicalEditor
          markdown={specifiedReason}
          displayTextEditor={notifyReportingParty}
          belowEditorChildren={
            <Box className={actionModalStyles.belowEditorBox}>
              <TranslatedResponseSection />
              <Typography className={actionModalStyles.optionalText}>
                {modalDetails[selectedAction]?.rteOptionalText}
              </Typography>
            </Box>
          }
          editorButtons={[
            <div
              key="editor-actions-spacer"
              className={actionModalStyles.editorActionsSpacer}
            />,
            <div
              key="editor-actions"
              className={actionModalStyles.editorActionsRow}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={isSubmitting || translation.isTranslating}
              >
                CANCEL
              </Button>
              <SubmitButton />
            </div>,
          ]}
        />
      </div>
    </RubiModal>
  );
};

export default ActionModal;
